import { generateDungeon } from "./generators/dungeon.js";
import { generateVillage } from "./generators/village.js";
import { generateWilderness } from "./generators/wilderness.js";
import { generateGraveyard } from "./generators/graveyard.js";
import { generateHub } from "./generators/hub.js";

/**
 * A THEME fully describes a kind of level: environment (lighting/fog), ground &
 * boundary look, a procedural generator, a prop palette, and an enemy pool.
 * `assets` maps a prop shape (or ground/boundary modelKey) to an AssetRegistry
 * key; when a model is missing, the placeholder `shape` is drawn instead.
 */
export const THEMES = {
  dungeon: {
    id: "dungeon", name: "Dungeon", generator: generateDungeon,
    env: { background: 0x0b0d12, fog: [0x0b0d12, 20, 56], ambient: [0x54648a, 0.34], hemi: [0x8ba0cf, 0x18140f, 0.34], key: [0xffd9a0, 0.55] },
    ground: { colors: [0x2a2f3a, 0x242832], modelKey: "dungeon/floor" },
    floorModels: ["dungeon/floor"],   // single uniform tile — variants sat at different heights (recessed pits)
    boundary: { shape: "wall", color: 0x333a48, height: 2.6, modelKey: "dungeon/wall" },
    wallModels: ["dungeon/wall", "dungeon/wall_window", "dungeon/wall_arched"],
    wallTorch: "dungeon/torch_wall",
    wallBanner: "dungeon/banner",
    doorKey: "dungeon/door",
    props: [
      { shape: "barrel", color: 0x7a5230, blocking: true, weight: 6, breakable: true, table: "barrel" },
      { shape: "crate", color: 0x8a6a3a, blocking: true, weight: 6, breakable: true, table: "crate" },
      { shape: "barrel_stack", color: 0x7a5230, blocking: true, weight: 3, breakable: true, table: "barrel" },
      { shape: "bottle", color: 0x6a8a5a, blocking: false, weight: 3, breakable: true, table: "pot" },
      { shape: "table", color: 0x7a5230, blocking: true, weight: 2, breakable: true, table: "crate" },
      { shape: "chest", color: 0xc9a24b, blocking: true, weight: 2, breakable: true, table: "chest" },
      { shape: "bed", color: 0x9a6a4a, blocking: true, weight: 2 },
      { shape: "pillar", color: 0x3a4150, blocking: true, weight: 3 },
      { shape: "bones", color: 0xd8d2c0, blocking: false, weight: 3 },
      { shape: "candle", color: 0xffcf6f, blocking: false, weight: 2 },
      { shape: "torch", color: 0xffb347, blocking: false, weight: 3 },
    ],
    propDensity: 0.13, modelScale: 0.5,
    heroKey: "characters/knight", enemyKey: "enemies/skel_minion",
    enemies: ["SkeletonWarrior", "SkeletonRogue", "SkeletonMage", "SkeletonMinion"],
    assets: {
      barrel: "dungeon/barrel", crate: "dungeon/crate", barrel_stack: "dungeon/barrel_stack",
      bottle: "dungeon/bottle", pillar: "dungeon/column", torch: "dungeon/torch",
      chest: "dungeon/chest", table: "dungeon/table", candle: "dungeon/candle", bed: "dungeon/bed",
      coins: "dungeon/coins", bookcase: "dungeon/bookcase", chest_gold: "dungeon/chest_gold",
      chest_large: "dungeon/chest_large",
    },
  },

  graveyard: {
    id: "graveyard", name: "Graveyard", generator: generateGraveyard,
    env: { background: 0x0b0f14, fog: [0x111823, 20, 58], ambient: [0x54648a, 0.4], hemi: [0x8ba0cf, 0x14140f, 0.4], key: [0x9fb4e0, 0.7] },
    ground: { colors: [0x2a3326, 0x232b20] },
    boundary: { shape: "fence", color: 0x3a3f46, height: 1.5 },
    props: [
      { shape: "gravestone", color: 0x8a8f96, blocking: true, weight: 8 },
      { shape: "cross", color: 0x9a9a90, blocking: true, weight: 3 },
      { shape: "coffin", color: 0x6b4a2a, blocking: true, weight: 2 },
      { shape: "deadtree", color: 0x4a3a2a, blocking: true, weight: 2 },
      { shape: "candle", color: 0xffcf6f, blocking: false, weight: 4 },
    ],
    propDensity: 0.06, modelScale: 1.5,
    heroKey: "characters/knight", enemyKey: "enemies/zombie",
    enemies: ["Skeleton", "Husk", "Creep", "Slime"],
    assets: { gravestone: "grave/gravestone", cross: "grave/cross", coffin: "grave/coffin", deadtree: "grave/deadtree", candle: "grave/candle", crypt: "grave/crypt" },
  },

  village: {
    id: "village", name: "Village", generator: generateVillage,
    env: { background: 0x1a2030, fog: [0x243044, 34, 82], ambient: [0xbfd0e8, 0.7], hemi: [0xcfe0ff, 0x4a4030, 0.6], key: [0xffe6b0, 1.25] },
    ground: { colors: [0x4a6b3a, 0x557a42] },
    boundary: { shape: "fence", color: 0x6b4a2a, height: 1.2 },
    props: [
      { shape: "building", color: 0x9a8060, blocking: true, weight: 0 },
      { shape: "well", color: 0x8a8a8a, blocking: true, weight: 2 },
      { shape: "cart", color: 0x7a5230, blocking: true, weight: 2 },
      { shape: "haybale", color: 0xcaa24a, blocking: true, weight: 3 },
      { shape: "crate", color: 0x8a6a3a, blocking: true, weight: 3 },
      { shape: "lamp", color: 0xffd27f, blocking: false, weight: 3 },
      { shape: "barrel", color: 0x7a5230, blocking: true, weight: 3 },
    ],
    propDensity: 0.04,
    heroKey: "characters/knight", enemyKey: "enemies/human",
    enemies: ["Bandit", "Husk", "Brute"],
  },

  wilderness: {
    id: "wilderness", name: "Wilderness", generator: generateWilderness,
    env: { background: 0x223018, fog: [0x2c3a1e, 34, 90], ambient: [0xc8e0a0, 0.75], hemi: [0xdfffbf, 0x3a3018, 0.7], key: [0xfff2c0, 1.35] },
    ground: { colors: [0x3f6a2e, 0x4a7a36] },
    boundary: { shape: "cliff", color: 0x4a3a26, height: 3.2 },
    props: [
      { shape: "tree_pine", color: 0x2e6b34, blocking: true, weight: 6 },
      { shape: "tree_round", color: 0x3f8040, blocking: true, weight: 5 },
      { shape: "tree_tall", color: 0x2e6b34, blocking: true, weight: 4 },
      { shape: "deadtree", color: 0x5a4a36, blocking: true, weight: 2 },
      { shape: "boulder", color: 0x7d7d75, blocking: true, weight: 4 },
      { shape: "boulder_large", color: 0x6d6d68, blocking: true, weight: 3 },
      { shape: "rock", color: 0x8d8d85, blocking: true, weight: 4 },
      { shape: "bush", color: 0x3a7038, blocking: false, weight: 10 },
      { shape: "bush2", color: 0x357034, blocking: false, weight: 9 },
      { shape: "bush3", color: 0x2f6a30, blocking: false, weight: 7 },
      { shape: "grass", color: 0x5a9040, blocking: false, weight: 14 },
      { shape: "grass2", color: 0x62984a, blocking: false, weight: 12 },
    ],
    propDensity: 0.26, modelScale: 1.0,
    heroKey: "characters/knight", enemyKey: "enemies/orc",
    enemies: ["Wolf", "Slime", "Creep"],
    assets: {
      tree_pine: "forest/tree_a", tree_round: "forest/tree_b", tree_tall: "forest/tree_tall", tree_d: "forest/tree_d",
      deadtree: "forest/deadtree",
      boulder: "forest/boulder", boulder2: "forest/boulder3", boulder3: "forest/boulder4", boulder_large: "forest/boulder2",
      rock: "forest/rock_md", rock2: "forest/rock_md2", rock_sm: "forest/rock_sm",
      bush: "forest/bush_a", bush2: "forest/bush_b", bush3: "forest/bush_d", grass: "forest/grass", grass2: "forest/grass2", grass3: "forest/grass3",
      // camp props
      campfire: "dungeon/campfire", bedroll: "dungeon/bedroll", keg: "dungeon/keg",
      crate: "dungeon/crate", barrel: "dungeon/barrel", barrel_large: "dungeon/barrel_large",
      chest: "dungeon/chest", coins: "dungeon/coins", banner: "dungeon/banner",
    },
  },

  hub: {
    id: "hub", name: "Eden", generator: generateHub,
    env: { background: 0x243044, fog: [0x2c3a52, 42, 120], ambient: [0xbfd0e8, 0.8], hemi: [0xcfe0ff, 0x4a4030, 0.65], key: [0xffe6b0, 1.25] },
    ground: { colors: [0x4a6b3a, 0x557a42] },
    customGround: true,            // Game lays a real tessellated hex-tile map (grass/road/water)
    boundary: { shape: "none" },   // no walls/fence — grass runs off-screen, invisible border stops the player
    props: [], propDensity: 0, modelScale: 1,
    heroKey: "characters/knight", enemyKey: "enemies/skel_minion", enemies: [],
    assets: {},
  },
};

// Active biomes in the rotation. The hub loads explicitly between levels;
// the village is parked for later.
const INACTIVE = ["hub", "village"];
export const THEME_LIST = Object.values(THEMES).filter((t) => !INACTIVE.includes(t.id));

/** Floor 1 is always the dungeon; after that random, no immediate repeat. */
export function pickTheme(floor, previousId) {
  if (floor <= 1) return THEMES.dungeon;
  const pool = THEME_LIST.filter((t) => t.id !== previousId);
  return pool[Math.floor(Math.random() * pool.length)];
}
